import { useEffect } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import { useAuthStore } from '../../store/useAuthStore'
import { useUserStore } from '../../store/useUserStore'
import AdminLayout from '../layout/AdminLayout'
import axiosInstance from '../../api/axios'
import { resolveAvatarSrc } from '../../utils/avatar'

interface ProtectedRouteProps {
  allowedStatus?: string[]
  requireProfile?: boolean
}

const ProtectedRoute = ({
  allowedStatus,
  requireProfile = false,
}: ProtectedRouteProps) => {
  const { isLoggedIn, user, updateUserPartial } = useAuthStore()
  const { userProfile, setUserProfile } = useUserStore()

  // 구형 아바타 경로를 신규 경로로 자동 마이그레이션
  useEffect(() => {
    if (
      user?.STATUS === 'U' &&
      user.NICKNAME &&
      user.AVATAR &&
      !user.AVATAR.includes('/avatar/characters/')
    ) {
      const newAvatarSrc = resolveAvatarSrc(user.AVATAR)
      axiosInstance
        .post('/user/profile/create', {
          nickname: user.NICKNAME,
          avatarImagePath: newAvatarSrc,
          ID: user.ID,
        })
        .then(() => {
          updateUserPartial({ AVATAR: newAvatarSrc })
        })
        .catch(() => {
          // fire-and-forget: 실패해도 로그인 흐름 유지
        })
    }
  }, [user?.ID])

  // 서버 프로필 데이터를 로컬 상태와 동기화
  useEffect(() => {
    if (user?.STATUS === 'U' && user.NICKNAME && user.AVATAR && !userProfile) {
      // 서버에는 프로필이 있는데 로컬 상태에는 없는 경우 동기화
      const avatarType = extractAvatarTypeFromPath(user.AVATAR)
      const avatarColor = extractAvatarColorFromPath(user.AVATAR)

      const profileData = {
        nickname: user.NICKNAME,
        avatarType,
        avatarColor,
        avatarImagePath: user.AVATAR,
      }

      setUserProfile(profileData as any)
    }
  }, [user, userProfile, setUserProfile])

  // 1. 로그인 체크
  if (!isLoggedIn) {
    return <Navigate to="/signin" replace />
  }

  // 2. 사용자 상태(STATUS) 체크
  if (allowedStatus && allowedStatus.length > 0) {
    const userStatus = user?.STATUS || 'U'

    if (!allowedStatus.includes(userStatus)) {
      // 권한이 없는 경우 해당 권한의 기본 페이지로 리다이렉트
      const getDefaultPathByStatus = () => {
        switch (userStatus) {
          case 'A':
            return '/dashboard'
          case 'M':
            return '/dashboard/manager'
          case 'P':
            return '/dashboard/parents'
          case 'O':
            return '/dashboard/individual'
          case 'U':
            return hasUserProfile(user, userProfile) ? '/solver' : '/userSignin'
          default:
            return '/unauthorized'
        }
      }

      return <Navigate to={getDefaultPathByStatus()} replace />
    }
  }

  // 3. 프로필 체크 (U 상태 사용자만)
  if (requireProfile && user?.STATUS === 'U') {
    if (!hasUserProfile(user, userProfile)) {
      return <Navigate to="/userSignin" replace />
    }
  }

  return <Outlet />
}

const hasUserProfile = (user: any, userProfile: any) => {
  if (user?.NICKNAME && user?.AVATAR) {
    return true
  }

  return false
}

const extractAvatarTypeFromPath = (path: string) => {
  // 신규 경로: /assets/avatar/characters/Buddy.png 등
  if (path.includes('/avatar/characters/')) {
    if (path.includes('Buddy')) return 'Buddy' as any
    if (path.includes('Explorer')) return 'Explorer' as any
    if (path.includes('Thinker')) return 'Thinker' as any
  }
  // 구형 경로: /assets/eye-gray-monster.png 등
  if (path.includes('eye-')) return 'eye-monster'
  if (path.includes('rabbit-')) return 'rabbit-monster'
  if (path.includes('smart-')) return 'smart-monster'
  return 'eye-monster'
}

const extractAvatarColorFromPath = (path: string) => {
  if (path.includes('-blue-')) return 'blue'
  if (path.includes('-gray-')) return 'gray'
  if (path.includes('-green-')) return 'green'
  if (path.includes('-red-')) return 'red'
  return 'blue'
}

export default ProtectedRoute
